import {
  RadioProps,
  useRadio,
  useRadioGroup,
  HStack,
  Box,
  useCheckbox,
  CheckboxProps,
  useCheckboxGroup,
} from '@chakra-ui/react';

import React from 'react';

// custom radio button for colors
function RadioColorButton(props: RadioProps) {
  const { getInputProps, getCheckboxProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Box as='label'>
      <input {...input} />
      <Box
        {...checkbox}
        bgColor={props.bgColor}
        cursor='pointer'
        borderWidth='1px'
        borderRadius='50%'
        boxShadow='md'
        _checked={{
          borderColor: 'teal.600',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        px={3}
        py={3}></Box>
    </Box>
  );
}

/**
 * Interface for custom radio props
 */
interface ControlCustomRadioProps {
  /** name of the radio group */
  name: string;
  /** default value */
  value: string;
  onChange: (value: string) => void;
  /** list of radio button options */
  options: string[];
}

// manages a group of color radio buttons
export function ColorRadio({ name, value, onChange, options }: ControlCustomRadioProps) {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value,
    onChange,
  });
  const group = getRootProps();
  return (
    <HStack {...group}>
      {options.map(v => {
        const radio = getRadioProps({ value: v });
        return (
          <RadioColorButton key={v} {...radio} bgColor={v} defaultChecked={v === value}>
            {v}
          </RadioColorButton>
        );
      })}
    </HStack>
  );
}

// custom radio button with a label
function RadioLabelButton(props: RadioProps) {
  const { getInputProps, getCheckboxProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Box as='label'>
      <input {...input} />
      <Box
        {...checkbox}
        cursor='pointer'
        borderWidth='1px'
        borderRadius='md'
        boxShadow='md'
        _checked={{
          bg: 'teal.600',
          color: 'white',
          borderColor: 'teal.600',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        px={5}
        py={3}>
        {props.children}
      </Box>
    </Box>
  );
}

// manages group of labeled radio buttons
export function LabelRadio({ name, value, onChange, options }: ControlCustomRadioProps) {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value,
    onChange,
  });

  const group = getRootProps();

  return (
    <HStack {...group}>
      {options.map(v => {
        const radio = getRadioProps({ value: v });
        return (
          <RadioLabelButton key={v} {...radio} isChecked={v == value}>
            {v}
          </RadioLabelButton>
        );
      })}
    </HStack>
  );
}

// custom checkbox button
function CustomCheckbox(props: CheckboxProps) {
  const { getInputProps, getCheckboxProps } = useCheckbox(props);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Box as='label'>
      <input {...input} />
      <Box
        {...checkbox}
        cursor='pointer'
        borderWidth='1px'
        borderRadius='md'
        boxShadow='md'
        _checked={{
          bg: 'teal.600',
          color: 'white',
          borderColor: 'teal.600',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        px={5}
        py={3}>
        {props.children}
      </Box>
    </Box>
  );
}

interface ControlCustomCheckboxProps {
  /** default value */
  value: string[];
  onChange: (value: string[]) => void;
  /** list of radio button options */
  options: string[];
}

// manages group of checkboxes
export function CustomCheckboxGroup({ value, onChange, options }: ControlCustomCheckboxProps) {
  const { getCheckboxProps } = useCheckboxGroup({
    onChange,
    defaultValue: value,
  });
  return (
    <HStack>
      {options.map(v => {
        const radio = getCheckboxProps({ value: v });
        return (
          <CustomCheckbox key={v} {...radio}>
            {v}
          </CustomCheckbox>
        );
      })}
    </HStack>
  );
}
